import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentStep, setCurrentContributionStep } from "@/store/wizard";
import { WizardStepSkeleton } from "../WizardStepSkeleton";
import { WizardStepError } from "../WizardStepError";
import Button from "@components/Common/Buttons/Button";
import { useSummaryData } from "./hooks/useSummaryData";
import BeneficiariesSection from "./BeneficiariesSection";
import GoalsSection from "./GoalsSection";
import ContributionSection from "./ContributionSection";
import SplitSection from "./SplitSection";
import PlanSection from "./PlanSection";
import { submitApplication } from "@/services/enrollment";
import { RootState } from "@/store";
import { handleApiError } from "@/utils/errorHandling";
import { customToast } from "@components/Common/Toast";
import { fetchInvestmentOptions } from "@/services/investmentOptions";
import { useServiceFee } from "@/hooks/useServiceFee";

interface InvestmentOptionRecord {
  portfolioName: string;
  portfiloShortName: string;
}

interface SummaryProps {
  handleNext: () => void;
  handlePrevious: () => void;
  isUserEnrolled?: boolean;
  onCancel?: () => void;
}

const Summary: React.FC<SummaryProps> = ({
  handleNext,
  handlePrevious,
  isUserEnrolled,
}) => {
  const dispatch = useDispatch();
  const { data, loading, error } = useSummaryData();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [investmentOptionsMapping, setInvestmentOptionsMapping] = useState<
    Map<string, string>
  >(new Map());

  const { serviceChargeAmount, serviceChargePaidBy } = useServiceFee();

  const {
    employeeID,
    id: userAccountID,
    sootchyEmployerID,
  } = useSelector((state: RootState) => state.user);

  const handleEdit = (stepNumber: number, contributionSubStep?: number) => {
    dispatch(setCurrentStep(stepNumber));
    if (contributionSubStep) {
      dispatch(setCurrentContributionStep(contributionSubStep));
    }
  };

  useEffect(() => {
    const fetchInvestmentMapping = async () => {
      try {
        const response = await fetchInvestmentOptions();
        const options = (response.data || []) as InvestmentOptionRecord[];

        const mapping = new Map<string, string>();
        options.forEach((option) => {
          mapping.set(option.portfolioName, option.portfiloShortName);
        });

        setInvestmentOptionsMapping(mapping);
      } catch (_err) {
        // investment options mapping is best-effort; submit flow handles missing mappings
      }
    };

    fetchInvestmentMapping();
  }, []);

  const handleSubmitApplication = async () => {
    if (!employeeID || !userAccountID || !sootchyEmployerID || !data) {
      setSubmitError("User information or summary data not available");
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      const formattedData = data.beneficiaries.map((beneficiary) => {
        const planData = data.plan.find(
          (p) => p.minorAccountID === beneficiary.minorAccountID,
        );

        const displayName = planData?.investmentOption || "";
        const investmentOptionID =
          investmentOptionsMapping.get(displayName) || displayName;

        return {
          userAccountID: userAccountID,
          minorAccountID: beneficiary.minorAccountID,
          employeeID: employeeID,
          sootchyEmployerID: Number(sootchyEmployerID),
          investmentOption: investmentOptionID,
          createdUserAccountID: userAccountID,
          sourceProcess: "WebApplication",
          changeReason: "Submit Application",
        };
      });

      // Check for incomplete data due to API failure
      const incompleteEntries = formattedData.filter(
        (entry) => !entry.investmentOption || entry.investmentOption === "",
      );

      if (incompleteEntries.length > 0) {
        throw new Error(
          "Application data is incomplete. Please refresh the page and try again, or contact support if the issue persists.",
        );
      }

      const response = await submitApplication(formattedData);

      if (response.status === 200) {
        customToast("Application submitted successfully!", "success");
        handleNext();
      } else {
        throw new Error(`Submission failed with status: ${response.status}`);
      }
    } catch (err: unknown) {
      handleApiError(err, "Unable to submit application", false, customToast);
      setSubmitError("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full mx-auto font-outfit sm:w-[640px]">
        <WizardStepSkeleton />
      </div>
    );
  }

  if (error || submitError) {
    return (
      <div className="w-full mx-auto font-outfit sm:w-[640px]">
        <WizardStepError
          step={6}
          handlePrevious={handlePrevious}
          errorMessage={error || submitError || "An error occurred"}
          hideStepCounter={true}
        />
      </div>
    );
  }

  return (
    <div className="w-full mx-auto font-outfit sm:w-[640px]">
      <div className="flex flex-col gap-[10px] text-[1rem]">
        <p className="font-semibold">
          Review your choices and click Finish whenever you're ready
        </p>
        <p className="mb-6">
          Confirm that the information you are submitting is accurate. You can
          edit any section if needed.
        </p>
      </div>

      <div className="bg-white rounded-[16px] shadow-introBox">
        <BeneficiariesSection
          data={data?.beneficiaries || []}
          onEdit={() => handleEdit(2)}
        />

        <GoalsSection data={data?.goals || []} onEdit={() => handleEdit(3)} />

        <ContributionSection
          data={data?.contribution}
          onEdit={() => handleEdit(4, 1)}
          serviceChargeAmount={serviceChargeAmount}
          serviceChargePaidBy={serviceChargePaidBy}
        />

        <SplitSection
          data={data?.split || []}
          onEdit={() => handleEdit(4, 2)}
        />

        <PlanSection data={data?.plan || []} onEdit={() => handleEdit(5)} />
      </div>

      <div
        className={`flex items-center space-x-4 lg:space-x-0 bg-white ${
          isUserEnrolled
            ? "gap-4"
            : "justify-between mt-[24px] md:shadow-md rounded-[16px] px-0 py-[32px] md:p-[32px]"
        }`}
      >
        <div className={`${isUserEnrolled ? "w-full" : "w-[218px]"}`}>
          <Button
            ariaLabel="previous"
            variant="secondary"
            label="Previous"
            iconAlignment="left"
            handleClick={handlePrevious}
          />
        </div>

        <div className={`${isUserEnrolled ? "w-full" : "w-[218px]"}`}>
          <Button
            iconVisibility="hide"
            ariaLabel="finish setup"
            label="Finish set-up"
            loading={isSubmitting}
            isDisabled={isSubmitting}
            handleClick={handleSubmitApplication}
          />
        </div>
      </div>
    </div>
  );
};

export default Summary;
